import { useBrowserStore } from "@/composables/useBrowserStore";

/**
 * Browser-store key for the Deepgram `language` used when transcribing
 * meeting recordings, namespaced per account (`useBrowserStore(accountId)`).
 * An empty or unknown value falls back to Automatic (`multi`). The key also
 * exists un-namespaced, written by older versions of the app; the first read
 * moves that legacy value into the reading account's key.
 */
export const MEETING_TRANSCRIPTION_LANGUAGE_KEY =
  "preferences/meetings/transcription:language";

/** Nova-3 multilingual code-switching (English, French, and eight others). */
export const DEFAULT_MEETING_TRANSCRIPTION_LANGUAGE = "multi";

export type MeetingTranscriptionLanguage = {
  code: string;
  label: string;
};

/**
 * Languages Nova-3 accepts. Regional variants are collapsed to one code
 * except where the writing system or dialect is a different product choice
 * (Chinese scripts, Flemish, Swiss German).
 *
 * @see https://developers.deepgram.com/docs/models-languages-overview
 */
export const MEETING_TRANSCRIPTION_LANGUAGES: MeetingTranscriptionLanguage[] = [
  { code: "multi", label: "Automatic" },
  { code: "af", label: "Afrikaans" },
  { code: "ar", label: "Arabic" },
  { code: "hy", label: "Armenian" },
  { code: "as", label: "Assamese" },
  { code: "be", label: "Belarusian" },
  { code: "bn", label: "Bengali" },
  { code: "bs", label: "Bosnian" },
  { code: "bg", label: "Bulgarian" },
  { code: "ca", label: "Catalan" },
  { code: "zh-HK", label: "Chinese (Cantonese)" },
  { code: "zh", label: "Chinese (Simplified)" },
  { code: "zh-TW", label: "Chinese (Traditional)" },
  { code: "hr", label: "Croatian" },
  { code: "cs", label: "Czech" },
  { code: "da", label: "Danish" },
  { code: "nl", label: "Dutch" },
  { code: "en", label: "English" },
  { code: "et", label: "Estonian" },
  { code: "fi", label: "Finnish" },
  { code: "nl-BE", label: "Flemish" },
  { code: "fr", label: "French" },
  { code: "ka", label: "Georgian" },
  { code: "de", label: "German" },
  { code: "de-CH", label: "German (Switzerland)" },
  { code: "el", label: "Greek" },
  { code: "gu", label: "Gujarati" },
  { code: "he", label: "Hebrew" },
  { code: "hi", label: "Hindi" },
  { code: "hu", label: "Hungarian" },
  { code: "id", label: "Indonesian" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "kn", label: "Kannada" },
  { code: "kk", label: "Kazakh" },
  { code: "ko", label: "Korean" },
  { code: "lv", label: "Latvian" },
  { code: "lt", label: "Lithuanian" },
  { code: "mk", label: "Macedonian" },
  { code: "ms", label: "Malay" },
  { code: "mr", label: "Marathi" },
  { code: "mn", label: "Mongolian" },
  { code: "ne", label: "Nepali" },
  { code: "no", label: "Norwegian" },
  { code: "ps", label: "Pashto" },
  { code: "fa", label: "Persian" },
  { code: "pl", label: "Polish" },
  { code: "pt", label: "Portuguese" },
  { code: "pa", label: "Punjabi" },
  { code: "ro", label: "Romanian" },
  { code: "ru", label: "Russian" },
  { code: "sr", label: "Serbian" },
  { code: "sk", label: "Slovak" },
  { code: "sl", label: "Slovenian" },
  { code: "es", label: "Spanish" },
  { code: "sv", label: "Swedish" },
  { code: "tl", label: "Tagalog" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "th", label: "Thai" },
  { code: "tr", label: "Turkish" },
  { code: "uk", label: "Ukrainian" },
  { code: "ur", label: "Urdu" },
  { code: "vi", label: "Vietnamese" },
];

const LANGUAGE_CODES = new Set(
  MEETING_TRANSCRIPTION_LANGUAGES.map((language) => language.code)
);

export function parseMeetingTranscriptionLanguage(value: unknown): string {
  return typeof value === "string" && LANGUAGE_CODES.has(value)
    ? value
    : DEFAULT_MEETING_TRANSCRIPTION_LANGUAGE;
}

export function getSelectedMeetingTranscriptionLanguage(
  accountId: string
): string {
  const store = useBrowserStore(accountId);
  store.migrate(MEETING_TRANSCRIPTION_LANGUAGE_KEY);
  return parseMeetingTranscriptionLanguage(
    store.get<unknown>(
      MEETING_TRANSCRIPTION_LANGUAGE_KEY,
      DEFAULT_MEETING_TRANSCRIPTION_LANGUAGE
    )
  );
}
